import { AlertTriangle } from "lucide-react";

/**
 * Confirm-on-exit dialog (P3). Rendered by editors that own dirty state
 * (tactics + whiteboard) when the user attempts to navigate away with
 * unsaved edits. Three exit paths:
 *
 *  - **Speichern & Verlassen** \u2014 commits the in-flight changes then closes.
 *  - **Verwerfen** \u2014 throws away local edits and closes immediately.
 *  - **Abbrechen** \u2014 stays in the editor.
 *
 * Pure presentational; all behaviour lives in the parent callbacks.
 */
export function UnsavedChangesDialog({
  open,
  saving,
  onSaveAndClose,
  onDiscard,
  onCancel,
}: {
  open: boolean;
  /** Disable the save button while a save mutation is in flight. */
  saving?: boolean;
  onSaveAndClose: () => void;
  onDiscard: () => void;
  onCancel: () => void;
}) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-120 flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm"
      onClick={onCancel}
      onKeyDown={(e) => {
        if (e.key === "Escape") onCancel();
      }}
      role="presentation"
    >
      <div
        className="glass-floating w-full max-w-md rounded-3xl border border-white/10 p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="unsaved-dialog-title"
      >
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-amber-400/15 text-amber-300">
            <AlertTriangle className="size-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="unsaved-dialog-title" className="text-body-md font-semibold leading-tight">
              Ungespeicherte Änderungen
            </h2>
            <p className="mt-1 text-body-sm text-muted-foreground">
              Du hast Änderungen, die noch nicht gespeichert sind. Wie möchtest du fortfahren?
            </p>
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-2">
          <button
            type="button"
            onClick={onSaveAndClose}
            disabled={saving}
            className="inline-flex h-10 items-center justify-center rounded-full bg-pitch-lime px-4 text-body-sm font-semibold text-black shadow-glow hover:brightness-110 disabled:opacity-60"
          >
            {saving ? "Speichere…" : "Speichern & Verlassen"}
          </button>
          <button
            type="button"
            onClick={onDiscard}
            className="inline-flex h-10 items-center justify-center rounded-full bg-rose-500/15 px-4 text-body-sm font-medium text-rose-200 hover:bg-rose-500/25"
          >
            Änderungen verwerfen
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex h-10 items-center justify-center rounded-full bg-white/6 px-4 text-body-sm hover:bg-white/10"
          >
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  );
}
